import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import jsQR from 'jsqr';
import { logger } from '../../utils/logger';
import './QrScanner.css';
import { scanQrCode } from '../../services/qrCodeService';
import QrCamera from './QrCamera';
import QrScanResult from './QrScanResult';

const QrScanner: React.FC = (): React.ReactElement => {
  const navigate = useNavigate();
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState<boolean>(false);
  const [cameraActive, setCameraActive] = useState<boolean>(false);
  const [scanResult, setScanResult] = useState<{
    success: boolean;
    message: string;
    name?: string;
    email?: string;
    applicationId?: string;
    attendeeType?: string;
    error?: string;
  } | null>(null);
  const [processing, setProcessing] = useState<boolean>(false);
  
  const scannerInterval = useRef<number | null>(null);
  
  // Stop the camera
  const stopCamera = useCallback(() => {
    if (videoRef.current && videoRef.current.srcObject) {
      logger.info('🛑 Stopping camera');
      const stream = videoRef.current.srcObject as MediaStream;
      const tracks = stream.getTracks();
      
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setCameraActive(false); // Ensure cameraActive is always set to false
  }, [setCameraActive]); // Removed logger from dependencies
  
  // Stop scanning for QR codes
  const stopScanning = useCallback(() => {
    if (scannerInterval.current !== null) {
      logger.info('🛑 Stopping QR code scanning');
      clearInterval(scannerInterval.current);
      scannerInterval.current = null;
    }
    setScanning(false);
    stopCamera(); // Always stop camera when scanning is stopped
  }, [stopCamera]);
  
  // Only clean up camera and scanning when unmounting
  useEffect(() => {
    return () => {
      stopScanning();
      stopCamera();
    };
  }, [stopScanning, stopCamera]);
  
  // Start the camera
  const startCamera = async () => {
    setError(null);
    
    try {
      logger.info('📸 Starting camera');
      const constraints = {
        video: { facingMode: 'environment' }
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setCameraActive(true);
      }
    } catch (err) {
      logger.error('❌ Error starting camera:', err);
      setError('Unable to access camera. Please make sure you have granted camera permissions.');
      stopCamera(); // Call stopCamera to ensure cleanup and setCameraActive(false)
    }
  };
  
  // Start scanning for QR codes
  const startScanning = async () => {
    // Always stop previous scanning/camera before starting new scan
    stopScanning();
    setScanResult(null);
    setError(null);
    // Set scanning to true immediately to show loading state
    setScanning(true);
    logger.info('🔍 Starting QR code scanning');
    try {
      await startCamera();
      // If startCamera failed, it would have thrown and stopCamera() called.
      // If we are here, camera might be active.
      if (!videoRef.current?.srcObject) {
        // This case implies startCamera internally failed but didn't throw in a way that startScanning's catch was hit,
        // or it set cameraActive to false. If camera is not truly active, don't proceed.
        // setError might have been set by startCamera.
        // If startCamera calls stopCamera on its own failure, then cameraActive will be false.
        // We can check cameraActive state here.
        if (!cameraActive) { // Check if camera was successfully activated
          logger.warn('⚠️ Camera not active after startCamera attempt, aborting scan start.');
          setScanning(false); // Ensure scanning state is false
          return;
        }
      }
      setScanning(true);
      
    // Set up scanning interval
      scannerInterval.current = window.setInterval(() => {
        if (!videoRef.current || !canvasRef.current) return;
        const video = videoRef.current;
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) return;
      
      // Set canvas dimensions to match video
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
      
      // Draw current video frame to canvas
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Get image data for QR code detection
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      
      // Attempt to detect QR code
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        if (code) {
          logger.info('🎯 QR code detected:', code.data);
        // Process the detected QR code
          processQrCode(code.data);
        }
      }, 500);
    } catch (err) {
      logger.error('❌ Error starting camera for scanning:', err);
      setError('Unable to access camera. Please make sure you have granted camera permissions.');
      setScanning(false);
      stopCamera(); // Ensure camera is stopped if an error occurs after it might have been started
    }
  };
  
  // Process a detected QR code
  const processQrCode = async (qrValue: string) => {
    if (processing) return; // Prevent multiple simultaneous processing
    setProcessing(true);
    setError(null);
    stopScanning(); // This will also stop the camera
    logger.info('🎟️ Processing QR code:', qrValue);
    try {
      // Extract applicationId from URL if it's a URL
      let applicationId = qrValue;
      try {
        const url = new URL(qrValue);
        const pathParts = url.pathname.split('/');
        applicationId = pathParts[pathParts.length - 1];
      } catch (err) {
        // Not a URL, use the value as is
        logger.info('Using raw QR code value as applicationId');
      }
      
      // Add more detailed error handling
      try {
        const result = await scanQrCode(applicationId);
        setScanResult(result);  // Always set scan result, whether success or error
      } catch (scanErr) {
        // Check for specific auth errors
        const errMessage = scanErr instanceof Error ? scanErr.message : 'Unknown error';
        
        if (errMessage.includes('Authentication required') || 
            errMessage.includes('Admin access required')) {
          logger.error('❌ Authentication error when scanning:', errMessage);
          
          // Create a user-friendly error message
          setScanResult({
            success: false,
            message: 'Authentication error',
            error: 'You need admin privileges to scan QR codes. Please contact the system administrator.'
          });
        } else {
          logger.error('❌ Error processing QR code:', scanErr);
          setScanResult({
            success: false,
            message: 'Failed to process QR code',
            error: errMessage
          });
        }
      }
    } catch (err) {
      logger.error('❌ Error in QR code processing:', err);
      // Create an error result
      setScanResult({
        success: false,
        message: 'Failed to process QR code. Please try scanning again.',
        error: err instanceof Error ? err.message : 'Unknown error'
      });
    } finally {
      setProcessing(false);
    }
  };

  // Reset the scanner
  const resetScanner = () => {
    setScanResult(null);
    setError(null);
    startScanning();
  };

  // Navigate to application status page
  const viewApplication = (applicationId: string) => {
    logger.info('🔍 Navigating to application status:', applicationId);
    navigate(`/eventapplication/${applicationId}`);
  };
  
  return (
    <div className="qr-scanner-page">
      <div className="scanner-container">
        <div className="video-container" style={{ position: 'relative' }}>
          {/* Camera and controls */}
          {!scanResult && (
            <QrCamera
              cameraActive={cameraActive}
              scanning={scanning}
              processing={processing}
              error={error}
              videoRef={videoRef}
              canvasRef={canvasRef}
              onStart={startScanning}
              onStop={stopScanning}
              onRetry={startCamera}
            />
          )}
          {/* Scan result overlay (inside video container) */}
          {scanResult && (
            <QrScanResult
              result={scanResult}
              onReset={resetScanner}
              onViewApplication={viewApplication}
            />
          )}
        </div>
        <div className="scanner-instructions">
          <h3>Instructions</h3>
          <ol>
            <li>Click "Start Scanning" to activate the scanner</li>
            <li>Hold the QR code within the frame</li>
            <li>Hold steady until the code is recognized</li>
            <li>The system will verify the code and display the result</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default QrScanner;